import { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus, AlertCircle, User, UserCog, HardHat, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { z } from 'zod';
import { BulkMembreData, EMPTY_MEMBRE, MEMBRE_TEMPLATES } from '@/types/bulk';
import { useBulkDraft } from '@/hooks/useBulkDraft';

// Schema de validation Zod pour une ligne de membre
const membreRowSchema = z.object({
  prenom: z.string().min(1, 'Requis').max(100, '100 car. max'),
  nom: z.string().max(100, '100 car. max').optional(),
  poste: z.string().max(100, '100 car. max').optional(),
  specialite: z.string().max(100, '100 car. max').optional(),
  equipe_id: z.string().optional(),
  taux_horaire: z.number().positive('Requis').max(1000, 'Max 1000€/h'),
  coefficient_charges: z.number().min(1, 'Min 1.0').max(3, 'Max 3.0'),
  heures_par_jour: z.number().min(1, 'Min 1h').max(12, 'Max 12h'),
});
import { useCreateMembres } from '@/hooks/useCreateMembres';
import { BulkMembreRow } from './BulkMembreRow';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { useModalProtection } from '@/hooks/useModalProtection';

interface Equipe {
  id: string;
  nom: string;
}

interface BulkMembreModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entrepriseId: string;
  equipes: Equipe[];
  onSuccess?: () => void;
  currentCount: number;
  maxCount: number | null; // null = illimité
}

export function BulkMembreModal({
  open,
  onOpenChange,
  entrepriseId,
  equipes,
  onSuccess,
  currentCount,
  maxCount,
}: BulkMembreModalProps) {
  useModalProtection(open);

  const { draft, setDraft, clearDraft, hasPendingDraft, restoreDraft } = useBulkDraft<BulkMembreData>({
    key: 'bulk_membres',
    entrepriseId,
  });

  const { createMembres, isLoading } = useCreateMembres();
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);

  // Initialiser avec une ligne vide à l'ouverture
  useEffect(() => {
    if (open) {
      if (hasPendingDraft) {
        setShowRestorePrompt(true);
      } else {
        setDraft([createEmptyRow()]);
      }
    }
  }, [open, hasPendingDraft]);

  const createEmptyRow = (): BulkMembreData => ({
    id: uuidv4(),
    ...EMPTY_MEMBRE,
  });

  const createFromTemplate = (template: keyof typeof MEMBRE_TEMPLATES): BulkMembreData => ({
    id: uuidv4(),
    ...EMPTY_MEMBRE,
    ...MEMBRE_TEMPLATES[template],
  });

  const handleRestore = () => {
    const restored = restoreDraft();
    if (!restored || restored.length === 0) {
      setDraft([createEmptyRow()]);
    }
    setShowRestorePrompt(false);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setDraft([createEmptyRow()]);
    setShowRestorePrompt(false);
  };

  const handleChange = (index: number, data: Partial<BulkMembreData>) => {
    const updated = [...draft];
    updated[index] = { ...updated[index], ...data };
    setDraft(updated);

    // Clear error for this field
    if (errors[index]) {
      const newErrors = { ...errors };
      Object.keys(data).forEach(key => {
        delete newErrors[index]?.[key];
      });
      setErrors(newErrors);
    }
  };

  const handleAdd = () => {
    if (maxCount && currentCount + draft.length >= maxCount) {
      return;
    }
    setDraft([...draft, createEmptyRow()]);
  };

  const handleAddTemplate = (template: keyof typeof MEMBRE_TEMPLATES) => {
    if (maxCount && currentCount + draft.length >= maxCount) {
      return;
    }
    setDraft([...draft, createFromTemplate(template)]);
  };

  const handleDuplicate = (index: number) => {
    if (maxCount && currentCount + draft.length >= maxCount) {
      return;
    }
    const source = draft[index];
    const duplicated: BulkMembreData = {
      id: uuidv4(),
      prenom: '', // Vider prénom/nom
      nom: '',
      poste: source.poste,
      specialite: source.specialite,
      equipe_id: source.equipe_id,
      taux_horaire: source.taux_horaire,
      coefficient_charges: source.coefficient_charges,
      heures_par_jour: source.heures_par_jour,
    };
    const updated = [...draft];
    updated.splice(index + 1, 0, duplicated);
    setDraft(updated);
  };

  const handleDelete = (index: number) => {
    if (draft.length <= 1) return;
    const updated = draft.filter((_, i) => i !== index);
    setDraft(updated);
  };

  const validate = (): boolean => {
    const newErrors: Record<number, Record<string, string>> = {};
    let isValid = true;

    draft.forEach((row, index) => {
      const result = membreRowSchema.safeParse({
        prenom: row.prenom.trim(),
        nom: row.nom?.trim() || undefined,
        poste: row.poste?.trim() || undefined,
        specialite: row.specialite?.trim() || undefined,
        equipe_id: row.equipe_id || undefined,
        taux_horaire: row.taux_horaire || 0,
        coefficient_charges: row.coefficient_charges || 0,
        heures_par_jour: row.heures_par_jour || 0,
      });

      if (!result.success) {
        const rowErrors: Record<string, string> = {};
        result.error.issues.forEach(issue => {
          const field = issue.path[0] as string;
          rowErrors[field] = issue.message;
        });
        newErrors[index] = rowErrors;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    // Vérification finale du quota
    const validRows = draft.filter(row => row.prenom.trim());
    if (maxCount && currentCount + validRows.length > maxCount) {
      toast.error(`Quota dépassé. Vous pouvez ajouter ${maxCount - currentCount} membre(s) maximum.`);
      return;
    }

    const success = await createMembres(validRows, entrepriseId);

    if (success) {
      clearDraft();
      onOpenChange(false);
      onSuccess?.();
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const remainingSlots = maxCount ? maxCount - currentCount : null;
  const canAddMore = !maxCount || currentCount + draft.length < maxCount;
  const validRowsCount = draft.filter(row => row.prenom.trim()).length;

  // Pulse du bouton "Ajouter un membre" quand le 1er membre est rempli
  const [pulseAddBtn, setPulseAddBtn] = useState(false);
  const prevValidCount = useRef(0);
  useEffect(() => {
    if (prevValidCount.current === 0 && validRowsCount >= 1) {
      setPulseAddBtn(true);
      setTimeout(() => setPulseAddBtn(false), 3600);
    }
    prevValidCount.current = validRowsCount;
  }, [validRowsCount]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Ajouter des membres</DialogTitle>
          {remainingSlots !== null ? (
            <p className="text-sm text-muted-foreground">
              Membres : {currentCount}/{maxCount} | Vous pouvez en ajouter {Math.max(0, remainingSlots)}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {currentCount} membre(s) actuellement
            </p>
          )}
        </DialogHeader>

        {/* Prompt de restauration de brouillon */}
        {showRestorePrompt && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>Un brouillon non sauvegardé a été trouvé. Voulez-vous le restaurer ?</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleDiscardDraft}>
                  Non, recommencer
                </Button>
                <Button size="sm" onClick={handleRestore}>
                  Oui, restaurer
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Templates rapides */}
        <div className="flex gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground self-center">Templates :</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleAddTemplate('ouvrier')}
            disabled={!canAddMore}
          >
            <User className="h-4 w-4 mr-1" />
            + Ouvrier (11€/h net)
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleAddTemplate('chef')}
            disabled={!canAddMore}
          >
            <UserCog className="h-4 w-4 mr-1" />
            + Chef d'équipe (14€/h net)
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleAddTemplate('conducteur')}
            disabled={!canAddMore}
          >
            <HardHat className="h-4 w-4 mr-1" />
            + Conducteur (22€/h net)
          </Button>
        </div>

        {/* En-têtes des colonnes */}
        <div className="grid grid-cols-[30px_1fr_1fr_1fr_140px_100px_80px_70px_auto] gap-2 px-3 text-xs text-white font-medium">
          <span>N°</span>
          <span>Prénom *</span>
          <span>Nom</span>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 cursor-help">Poste <Info className="h-3 w-3 opacity-50" /></span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p>Poste = fonction dans l'entreprise (ex: chef d'équipe). Spécialité = compétence technique (ex: plaquiste).</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {false && <span>Spécialité</span>}
          <span>Équipe</span>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 cursor-help">Taux net * <Info className="h-3 w-3 opacity-50" /></span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p>Salaire net horaire de l'ouvrier (ce qu'il touche sur sa fiche de paie). Ex: 11€/h pour un ouvrier.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 cursor-help">Coef * <Info className="h-3 w-3 opacity-50" /></span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p>Ce coefficient transforme le salaire net en coût employeur réel. Il inclut les charges salariales, patronales, CIBTP, ProBTP, etc. La valeur 1.85 convient à la majorité des entreprises BTP. Pour trouver votre coefficient exact : divisez le coût total employeur (ligne du bas de la fiche de paie) par le salaire net. Exemple : fiche de paie = 2590€ coût employeur, 1400€ net → 2590/1400 = 1.85</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span>h/j</span>
          <span>Actions</span>
        </div>
        {/* Liste des membres */}
        <div className="flex-1 overflow-y-auto space-y-2 py-2">
          {draft.map((row, index) => (
            <BulkMembreRow
              key={row.id}
              index={index}
              data={row}
              equipes={equipes}
              onChange={handleChange}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
              canDelete={draft.length > 1}
              errors={errors[index] || {}}
              isFirst={index === 0}
            />
          ))}
        </div>

        {/* Bouton ajouter */}
        <Button
          type="button"
          variant="outline"
          onClick={handleAdd}
          disabled={!canAddMore}
          className={`w-full bg-white text-black font-semibold hover:bg-gray-100 hover:text-primary hover:border-primary ${pulseAddBtn ? 'animate-pulse' : ''}`}
          style={pulseAddBtn ? { animationIterationCount: 3, animationDuration: '1.2s' } : undefined}
        >
          <Plus className="h-4 w-4 mr-2" />
          Ajouter un membre
        </Button>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || validRowsCount === 0}
          >
            {isLoading ? 'Ajout...' :
             validRowsCount === 0 ? 'Remplir au moins un prénom' :
             validRowsCount === 1 ? 'Ajouter le membre' :
             `Ajouter ${validRowsCount} membres`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
